<template>
  <div v-click-outside="closeLayout" class="user-profile" :class="{ 'toggled': toggled, 'small': !allowAccess }">

    <!-- Short user info -->
    <div class="top-wrapper">
      <div class="user-wrapper">
        <div class="user-photo"></div>
        <div class="user-info">
          <div class="user-name">{{ userName }}</div>
          <div class="user-type">{{ user.user_type }}</div>
        </div>
      </div>

      <!-- Open extend info button -->
      <div @click="toggle" class="more-btn">
        <span class="dot tr"></span>
        <span class="dot"></span>
        <span class="dot tr"></span>
      </div>
    </div>

    <!-- Extend info -->
    <div class="elements-wrapper">
      <div class="item profile-settings" @click="modals.showSettingsUser = true">
        <svg class="icon">
          <use :xlink:href="`/svg/sprite.svg?v=${Date.now()}#icon-profile-settings`" />
        </svg>
        <span class="label">{{ 'sidebar.profile_settings' | translate }}</span>
      </div>

      <v-dialog max-width="350" v-model="modals.showSettingsUser">
        <settings-user-modal @close="modals.showSettingsUser = false" />
      </v-dialog>

      <color-indication-dialog 
        v-model="modals.showColorIndication"
        @close="onColorIndicationClose" 
      />

      <!-- Admin section with accordion -->
      <div v-if="allowAccess" class="menu-items">
        <template v-for="item in visibleMenuItems">
          <!-- Item with submenu (accordion) -->
          <div v-if="item.submenu" :key="'accordion-' + item.id" class="accordion-item">
            <div 
              class="item accordion-header" 
              :class="{ 'active': expandedItems[item.id] }"
              @click="toggleAccordion(item.id)">
              <svg class="icon">
                <use :xlink:href="`/svg/sprite.svg?v=${Date.now()}#${item.icon}`" />
              </svg>
              <span class="label">{{ item.title | translate }}</span>
              <span class="arrow" :class="{ 'rotated': expandedItems[item.id] }"></span>
            </div>

            <transition name="accordion">
              <div v-show="expandedItems[item.id]" class="accordion-content">
                <template v-for="subitem in item.submenu">
                    <!-- Color indication item - opens modal based on permissions -->
                    <div 
                      v-if="isColorIndicationItem(subitem)"
                      :key="'subitem-' + subitem.id"
                      class="item subitem"
                      @click="openColorIndicationModal()">
                      <svg class="icon icon-small">
                        <use :xlink:href="`/svg/sprite.svg?v=${Date.now()}#${subitem.icon}`" />
                      </svg>
                      <span class="label">{{ subitem.title | translate }}</span>
                    </div>
                    
                    <!-- Regular subitems - router links -->
                    <router-link 
                      v-else-if="!isColorIndicationItem(subitem)"
                      :key="'subitem-' + subitem.id" 
                      :to="subitem.path"
                      class="item subitem"
                      @click.native="closeLayout">
                      <svg class="icon icon-small">
                        <use :xlink:href="`/svg/sprite.svg?v=${Date.now()}#${subitem.icon}`" />
                      </svg>
                      <span class="label">{{ subitem.title | translate }}</span>
                    </router-link>
                  </template>
              </div>
            </transition>
          </div>

          <!-- Regular item without submenu -->
          <router-link v-else :key="'link-' + item.id" :to="item.path" class="item profile-settings"
            @click.native="closeLayout">
            <svg class="icon">
              <use :xlink:href="`/svg/sprite.svg?v=${Date.now()}#${item.icon}`" />
            </svg>
            <span class="label">{{ item.title | translate }}</span>
          </router-link>
        </template>
      </div>


    </div>

    <div class="footer">
      <!-- Logout -->
      <div @click="logout" class="item item-exit">
        <span class="i-admin-logout"></span>
        <span class="label">{{ 'base.logout' | translate }}</span>
      </div>
      <div class="footer--item">{{ appVersion }} | {{ updateDate }}</div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { ADMIN_SIDEBAR, EMPLOYEE_SIDEBAR, LANG_VAR_LOCAL_STORAGE_KEY } from '@/util/consts.js'

import SettingsUserModal from './modals/SettingsUserModal'
import ColorIndicationDialog from './modals/ColorIndicationDialog.vue'

export default {
  name: 'ClientsPanels',

  components: {
    SettingsUserModal,
    ColorIndicationDialog
  },

  mounted () {
    this.loadData()
  },

  data: (vm) => ({
    toggled: false,
    user: {
      first_name: '',
      last_name: '',
      photo: '',
      user_type: ''
    },
    modals: {
      showSettingsUser: false,
      showColorIndication: false
    },
    expandedItems: {} // Track which accordion items are expanded
  }),

  methods: {
    async loadData () {
      const { data } = await api.account.get()
      this.user.first_name = data.first_name
      this.user.last_name = data.last_name
      this.user.photo = data.photo
      this.user.user_type = data.user_type

      const lang = localStorage.getItem(LANG_VAR_LOCAL_STORAGE_KEY) || data.profile.language
      await this.$store.dispatch('account/setLang', lang)

      if (localStorage.getItem(LANG_VAR_LOCAL_STORAGE_KEY) !== data.profile.language) {
        api.account.update({ profile: { language: lang } })
      }
    },

    toggleAccordion (itemId) {
      this.$set(this.expandedItems, itemId, !this.expandedItems[itemId])
    },

    isColorIndicationItem (subitem) {
      // Check if path contains 'color-indication'
      return subitem.path && subitem.path.includes('color-indication')
    },

    isFarmSettingsItem (subitem) {
      // Items that belong to Farm Settings
      const farmSettingsPaths = ['mixers', 'color-indication', 'blanks']
      return farmSettingsPaths.some(path => subitem.path && subitem.path.includes(path))
    },

    shouldShowMenuItem (item) {
      const route = this.$route

      const hasFarmId = !!route.params.farm_id
      const hasClientId = !!(route.params.company_id || route.params.client_id)

      if (item.id === 1) {
        return hasClientId || route.path.startsWith('/app/admin/')
      }
      // Farm Settings (id: 2) - show only when we have farm_id
      if (item.id === 2) {
        return hasFarmId
      }
      // Other items - show always
      return true
    },

    processMenuItems (items) {
      const clientId = this.$route.params.client_id || this.$store.getters.clientId
      const farmId = this.$route.params.farm_id

      return items
        .filter(item => this.shouldShowMenuItem(item))
        .map(item => {
          // Process items with submenu
          if (item.submenu) {
            const processedSubmenu = item.submenu.map(subitem => {
              let path

              // Special handling for color-indication - requires farm_id
              if (subitem.path === 'color-indication') {
                if (!this.allowColorIndicationView && !this.allowColorIndicationChange) {
                  return null
                }
                path = farmId ? `/app/clients/${clientId}/farms/${farmId}/admin/${subitem.path}` : '#'
              } else if (subitem.path === 'mixer-db') {
                if (!this.isAdmin) return null
                path = `/app/${subitem.path}`
              } else {
                // Company settings items
                path = clientId
                  ? `/app/clients/${clientId}/admin/${subitem.path}`
                  : `/app/admin/${subitem.path}`
              }
              return { ...subitem, path }
            }).filter(Boolean)

            return {
              ...item,
              submenu: processedSubmenu
            }
          }

          // Process regular items
          let path
          if (item.path === 'mixer-db') {
            if (!this.isAdmin) return null
            path = `/app/${item.path}`
          } else {
            path = clientId
              ? `/app/clients/${clientId}/admin/${item.path}`
              : `/app/admin/${item.path}`
          }

          return { ...item, path }
        })
        .filter(item => item && item.title !== 'base.blanks')
    },

    toggle () {
      this.toggled = !this.toggled
    },

    openColorIndicationModal () {
      if (!this.allowColorIndicationChange) {
        return
      }
      this.modals.showColorIndication = true
    },

    onColorIndicationClose () {
      this.modals.showColorIndication = false
      this.$nextTick(() => {
        this.closeLayout()
      })
    },

    openSettingsDialog () {
      this.toggled = false
      this.$emit('open')
    },

    closeLayout () {
      if (this.toggled) {
        this.toggled = false
      }
    },

    logout () {
      this.$store.dispatch('setClientId', null)
      this.$store.dispatch('account/logout')
      this.$router.push('/auth')
    }
  },

  computed: {
    allowAccess () {
      return this.isClient || this.isAdmin || this.isStaff
    },
    allowColorIndicationView () {
      return this.hasPermission('monitoring_farm', 'view')
    },
    allowColorIndicationChange () {
      return this.hasPermission('monitoring_farm', 'change')
    },
    visibleMenuItems () {
      const sidebar = this.user.user_type === 'employee' ? EMPLOYEE_SIDEBAR : ADMIN_SIDEBAR
      return this.processMenuItems(sidebar)
    },
    userName () {
      const name = this.user.first_name + ' ' + this.user.last_name
      return name.length > 12 ? name.substring(0, 12) + '...' : name
    },

    updateDate () {
      return process.env.VUE_APP_UPDATE_DATE || 'mode'
    },

    appVersion () {
      return process.env.VUE_APP_VERSION || 'dev'
    }
  }
}
</script>

<style lang="scss" scoped>
.user-profile {
  position: absolute;
  top: 0;
  right: 0;
  height: 71px;
  width: 295px;
  padding-top: 1rem;
  transition: .4s;
  overflow: hidden;
  z-index: 600;
  border-bottom-left-radius: 1rem;
}

.arrow {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  transition: transform 0.2s ease;
  user-select: none;
  position: relative;

  &::before {
    content: '';
    display: block;
    width: 1rem;
    height: 0.7rem;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 10 7' fill='%23000' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M9.70711 0.792893C10.0976 1.18342 10.0976 1.81658 9.70711 2.20711L5 6.91421L0.292893 2.20711C-0.0976315 1.81658 -0.0976315 1.18342 0.292893 0.792893C0.683417 0.402369 1.31658 0.402369 1.70711 0.792893L5 4.08579L8.29289 0.792893C8.68342 0.402369 9.31658 0.402369 9.70711 0.792893Z'%3E%3C/path%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: contain;
  }

  &.rotated::before {
    transform: rotate(180deg);
  }
}

.icon {
  height: 26px;
  width: 26px;
  flex-shrink: 0;
}

.icon-small {
  height: 26px;
  width: 26px;
}

.user-profile.toggled {
  background: #fff;
  height: 680px;
  max-height: 90vh;
  width: 328px;
  padding-top: 16px;
  box-shadow: 0 1px 14px 0 rgba(0, 0, 0, 0.19);
  display: flex;
  flex-direction: column;
}

.user-profile.toggled.small {
  height: 355px;
}

.top-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 25px;
  padding-left: 20px;
}

.more-btn {
  cursor: pointer;
  width: 14px;
  display: inline-block;
  padding-left: 10px;
}

.more-btn .dot {
  display: block;
  height: 4px;
  width: 4px;
  margin: 4px 0;
  border-radius: 50%;
  background: #898989;
  transform: translateX(0);
  transition: .5s;
}

.user-profile.toggled .dot {
  background: #111;
}

.user-profile.toggled .dot.tr {
  transform: translateX(-10px);
}

.user-info {
  transform: translate(0, -9px);
}

.user-info,
.user-photo {
  display: inline-block;
}

.user-info .user-name {
  padding-top: 6px;
  color: #233c47;
  font-size: 14px;
  font-weight: 700;
}

.user-info .user-type {
  padding-top: 2px;
  color: #3f5c66;
  font-size: 13px;
}

.user-photo {
  height: 46px;
  width: 46px;
  margin-right: 15px;
  border-radius: 50%;
  background: #233c47;
}

.elements-wrapper {
  flex: 1;
  opacity: 0;
  display: flex;
  flex-direction: column;
  transition: .5s;
  padding-top: 10px;
  padding-left: 20px;
  padding-right: 15px;
  padding-bottom: 20px;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  scrollbar-width: thin;
  scrollbar-color: #59CC56 transparent;

  &::-webkit-scrollbar {
    width: 0.6rem;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #59CC56;
    border-radius: 0.3rem;
    border: 0.1rem solid transparent;
  }

  &:hover::-webkit-scrollbar-thumb {
    background-color: #4ab83f;
  }
}


.elements-wrapper .item {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.elements-wrapper .item .label {
  font-size: 15px;
  margin-left: 1rem;
  flex: 1;
}

/* Profile settings styling */
.profile-settings {
  height: 46px;
  padding: 10px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: background 0.3s;
  text-decoration: unset;
}

.profile-settings.router-link-active {
  background: #DDF2E3;
}

.profile-settings:hover {
  background: #eee;
}


/* Accordion styles */
.accordion-item {
  margin-bottom: 0;
}

.accordion-header {
  position: relative;
  padding: 10px 16px;
  height: 46px;
  border-radius: 8px;
  margin-bottom: 8px;
  transition: background 0.3s;
}

.accordion-header:hover {
  background: #EEEEEE;
}

.accordion-header.active {
  background: #DDF2E3;
}

.accordion-content {
  padding-left: 20px;
  overflow: hidden;
}

.subitem svg {
  color: #1da643;
}

.subitem {
  padding: 10px 16px;
  margin-bottom: 8px !important;
  font-size: 15px;
  text-decoration: unset;
  height: 46px;
  transition: background 0.3s;
  border-radius: 10px;
}

.subitem:hover {
  background: #eee;
  color: #000;
}

.subitem .label {
  margin-left: 1.5rem;
}

.subitem.router-link-active {
  background: #DDF2E3;
}

/* Accordion transition */
.accordion-enter-active,
.accordion-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
}

.accordion-enter,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}

.user-profile.toggled .elements-wrapper {
  opacity: 1;
}

.footer--item {
  font-size: 10px;
  font-weight: 300;
}

.item-exit {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.footer {
  background: #fff;
  padding-left: 2rem;
  padding-right: 1.5rem;
  border-top: 1px solid #D8D8D8;
  padding-bottom: 18px;
  padding-top: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  margin-top: auto;
}
</style>